import { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { apiClient } from "../../../api/httpClient";
import { toast } from "react-toastify";
import {
  toIssue,
  useComplianceValidationSummary,
  handleDeleteDraft,
} from "../../../globals/utils/homeHelpers";

export const HOME_DATA_PAGE_SIZE = 5;

function buildBondsFeedUrl(page, pageSize, pgId) {
  if (pgId) {
    return `/bonds-feed/?professional_guardian_id=${pgId}&page=${page}&page_size=${pageSize}`;
  }
  return `/bonds-feed/?page=${page}&page_size=${pageSize}`;
}

function buildMissingIndemnityUrl(pgId) {
  if (pgId) {
    return `/missing-indemnity-bonds?professional_guardian_id=${pgId}`;
  }
  return "/missing-indemnity-bonds";
}

/**
 * Shared drafts, activity feed, and indemnity state for IU/PG/LF home dashboards.
 * @param {object} options
 * @param {string|number|null} options.userId - Authenticated user id for draft cache (PG/IU)
 * @param {string|number|null} [options.pgId] - When set, scopes activity/indemnity to a PG
 * @param {string|number|null} [options.scopeId] - Generic scope id when using custom urlBuilders
 * @param {boolean} [options.enabled=true] - Gate activity/indemnity fetches (e.g. until pg resolves)
 * @param {(form: FormData, ctx: { pgId: string|number|null, userId: string|number|null, scopeId: string|number|null }) => void} [options.appendIndemnityFormFields]
 * @param {object} [options.urlBuilders] - Custom URL builders for law-firm home
 * @param {(page: number, pageSize: number, scopeId: string|number) => string} [options.urlBuilders.buildBondsFeedUrl]
 * @param {(scopeId: string|number) => string} [options.urlBuilders.buildMissingIndemnityUrl]
 * @param {(scopeId: string|number) => string} [options.urlBuilders.buildDraftsUrl]
 */
export function useHomeDataBase({
  userId,
  pgId = null,
  scopeId = null,
  enabled = true,
  appendIndemnityFormFields,
  urlBuilders = null,
}) {
  const location = useLocation();

  const [cachedBonds, setCachedBonds] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = HOME_DATA_PAGE_SIZE;
  const [totalCount, setTotalCount] = useState(0);
  const [hasMoreActivities, setHasMoreActivities] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [missingIndemnityBonds, setMissingIndemnityBonds] = useState([]);
  const [loadingIndemnity, setLoadingIndemnity] = useState(false);
  const [markingIndemnity, setMarkingIndemnity] = useState(null);

  const [indemnityDialogOpen, setIndemnityDialogOpen] = useState(false);
  const [selectedBondForIndemnity, setSelectedBondForIndemnity] = useState(null);
  const [indemnityOption, setIndemnityOption] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationResults, setValidationResults] = useState([]);
  const [processingOpen, setProcessingOpen] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const complianceFlags = useComplianceValidationSummary(validationResults);

  const resolvedScopeId = urlBuilders ? scopeId : pgId;
  const dataEnabled = urlBuilders ? Boolean(scopeId) && enabled : enabled;

  const resolveBondsFeedUrl = (pageNum, size) =>
    urlBuilders?.buildBondsFeedUrl
      ? urlBuilders.buildBondsFeedUrl(pageNum, size, resolvedScopeId)
      : buildBondsFeedUrl(pageNum, size, pgId);

  const resolveMissingIndemnityUrl = () =>
    urlBuilders?.buildMissingIndemnityUrl
      ? urlBuilders.buildMissingIndemnityUrl(resolvedScopeId)
      : buildMissingIndemnityUrl(pgId);

  useEffect(() => {
    const loadDrafts = async () => {
      if (urlBuilders?.buildDraftsUrl) {
        if (!resolvedScopeId) return;
        try {
          setLoadingDrafts(true);
          const rows = await apiClient(
            urlBuilders.buildDraftsUrl(resolvedScopeId),
            "GET"
          );
          setCachedBonds(Array.isArray(rows) ? rows : []);
        } catch (e) {
          console.error("Failed to load cached bonds", e);
          setCachedBonds([]);
        } finally {
          setLoadingDrafts(false);
        }
        return;
      }
      if (!userId) return;
      try {
        setLoadingDrafts(true);
        const rows = await apiClient(`/bond-cache-requester/${userId}/`, "GET");
        setCachedBonds(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.error("Failed to load cached bonds", e);
        setCachedBonds([]);
      } finally {
        setLoadingDrafts(false);
      }
    };
    loadDrafts();
  }, [userId, resolvedScopeId, urlBuilders]);

  useEffect(() => {
    if (!dataEnabled) return;
    const loadActivities = async (reset = true) => {
      try {
        setLoadingActivities(true);
        const targetPage = reset ? 1 : page;
        const data = await apiClient(
          resolveBondsFeedUrl(targetPage, pageSize),
          "GET"
        );
        const results = Array.isArray(data?.results) ? data.results : [];
        if (reset) {
          setActivities(results);
          setPage(1);
        } else {
          setActivities((prev) => [...prev, ...results]);
        }
        const count = Number(data?.count || 0);
        setTotalCount(count);
        setHasMoreActivities(targetPage * pageSize < count);
      } catch (e) {
        console.error("Failed to load recent activity", e);
        if (reset) setActivities([]);
        setHasMoreActivities(false);
      } finally {
        setLoadingActivities(false);
      }
    };
    loadActivities(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataEnabled, pgId, resolvedScopeId, urlBuilders]);

  const loadMissingIndemnity = useCallback(async () => {
    if (!dataEnabled) return;
    try {
      setLoadingIndemnity(true);
      const data = await apiClient(resolveMissingIndemnityUrl(), "GET");
      setMissingIndemnityBonds(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load missing indemnity bonds", e);
      setMissingIndemnityBonds([]);
    } finally {
      setLoadingIndemnity(false);
    }
  }, [dataEnabled, pgId, resolvedScopeId, urlBuilders]);

  useEffect(() => {
    loadMissingIndemnity();
  }, [location.pathname, loadMissingIndemnity]);

  const handleMarkIndemnityClick = useCallback((bond) => {
    setSelectedBondForIndemnity(bond);
    setIndemnityDialogOpen(true);
    setIndemnityOption(null);
    setSelectedFiles([]);
  }, []);

  const handleDeleteDraftClick = useCallback(
    (bondCacheId) =>
      handleDeleteDraft(bondCacheId, apiClient, setCachedBonds, {
        setDeletingId,
      }),
    []
  );

  const handleMarkIndemnityOffline = useCallback(async () => {
    if (!selectedBondForIndemnity) return;
    try {
      setMarkingIndemnity(selectedBondForIndemnity.bond_id);
      await apiClient("/set-indemnity-received/", "POST", {
        bond_id: selectedBondForIndemnity.bond_id,
        bond_type: selectedBondForIndemnity.bond_type.toLowerCase(),
      });
      toast.success("Indemnity marked as received successfully");
      setIndemnityDialogOpen(false);
      setSelectedBondForIndemnity(null);
      await loadMissingIndemnity();
    } catch (e) {
      console.error("Failed to mark indemnity as received", e);
      toast.error("Failed to mark indemnity as received");
    } finally {
      setMarkingIndemnity(null);
    }
  }, [selectedBondForIndemnity, loadMissingIndemnity]);

  const handleFilesSelected = useCallback((event) => {
    const all = Array.from(event.target.files || []);
    const files = all.slice(0, 4);
    const pdfsOnly = files.filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    );
    if (pdfsOnly.length !== files.length) {
      toast.error("Only PDF files are allowed.");
    }
    setSelectedFiles(pdfsOnly);
  }, []);

  const handleClearFiles = useCallback(() => setSelectedFiles([]), []);

  const handleRemoveFile = useCallback((index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleViewFile = useCallback((file) => {
    try {
      const url = URL.createObjectURL(file);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      console.error("Preview failed", e);
      toast.error("Unable to preview file.");
    }
  }, []);

  const handleSubmitIndemnityFiles = useCallback(async () => {
    if (!selectedBondForIndemnity?.bond_id) {
      toast.error("Missing bond information.");
      return;
    }
    if (!selectedFiles.length) {
      toast.error("Please select at least one PDF to upload.");
      return;
    }
    setIsSubmitting(true);
    setProcessingOpen(true);
    try {
      const form = new FormData();
      selectedFiles.forEach((f) => form.append("files", f));
      form.append("use_hg_sales_key", "false");
      const res = await apiClient("/indemnity-compliance-check/", "POST", form, {}, {});
      const resultsArr = Array.isArray(res?.results) ? res.results : [];
      const mapped = resultsArr.map((r, idx) => {
        const name =
          r?.file_name || selectedFiles[idx]?.name || `file_${idx + 1}.pdf`;
        const base = toIssue(name, r);
        const sz = selectedFiles[idx]?.size
          ? Math.max(1, Math.round(selectedFiles[idx].size / 1024))
          : null;
        return { ...base, sizeKb: sz };
      });
      setValidationResults(mapped);
      setValidationOpen(true);
    } catch (e) {
      console.error("Compliance check failed", e);
      toast.error(e?.error || "Failed to analyze indemnity PDF(s).");
    } finally {
      setIsSubmitting(false);
      setProcessingOpen(false);
    }
  }, [selectedBondForIndemnity?.bond_id, selectedFiles]);

  const handleFinalizeIndemnitySubmit = useCallback(async () => {
    if (!selectedBondForIndemnity?.bond_id || !selectedFiles.length) return;
    setIsFinalizing(true);
    try {
      const form = new FormData();
      selectedFiles.slice(0, 4).forEach((f) => form.append("files", f));
      form.append("bond_id", String(selectedBondForIndemnity.bond_id));
      if (appendIndemnityFormFields) {
        appendIndemnityFormFields(form, { pgId, userId, scopeId: resolvedScopeId });
      }
      form.append("status", "pending");
      await apiClient("/manually-signed-indemnities/", "POST", form, {}, {});

      await apiClient("/set-indemnity-received/", "POST", {
        bond_id: selectedBondForIndemnity.bond_id,
        bond_type: selectedBondForIndemnity.bond_type.toLowerCase(),
      });

      toast.success("Indemnity document(s) submitted successfully.");
      setSelectedFiles([]);
      setValidationOpen(false);
      setIndemnityDialogOpen(false);
      setSelectedBondForIndemnity(null);
      await loadMissingIndemnity();
    } catch (e) {
      console.error("Submit failed", e);
      toast.error(e?.error || "Failed to submit indemnity files.");
    } finally {
      setIsFinalizing(false);
    }
  }, [
    selectedBondForIndemnity,
    selectedFiles,
    appendIndemnityFormFields,
    pgId,
    userId,
    resolvedScopeId,
    loadMissingIndemnity,
  ]);

  const loadMoreActivities = useCallback(async () => {
    if (!dataEnabled) return;
    const nextPage = page + 1;
    setPage(nextPage);
    try {
      setLoadingActivities(true);
      const data = await apiClient(
        resolveBondsFeedUrl(nextPage, pageSize),
        "GET"
      );
      const results = Array.isArray(data?.results) ? data.results : [];
      setActivities((prev) => [...prev, ...results]);
      const count = Number(data?.count || totalCount || 0);
      setTotalCount(count);
      setHasMoreActivities(nextPage * pageSize < count);
    } catch (e) {
      console.error("Failed to load more activity", e);
      setHasMoreActivities(false);
    } finally {
      setLoadingActivities(false);
    }
  }, [dataEnabled, page, pageSize, pgId, resolvedScopeId, totalCount, urlBuilders]);

  const closeIndemnityDialog = useCallback(() => {
    setIndemnityDialogOpen(false);
    setSelectedBondForIndemnity(null);
    setIndemnityOption(null);
    setSelectedFiles([]);
  }, []);

  const sortedCachedBonds = useMemo(
    () =>
      Array.isArray(cachedBonds)
        ? [...cachedBonds].sort(
            (a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0)
          )
        : [],
    [cachedBonds]
  );

  const recentActiveCount = useMemo(() => {
    let count = 0;
    for (const it of activities) {
      if (String(it?.status || "").toLowerCase() === "active") count++;
      else break;
    }
    return count;
  }, [activities]);

  return {
    drafts: {
      cachedBonds,
      sortedCachedBonds,
      loadingDrafts,
      deletingId,
      handleDeleteDraftClick,
    },
    activity: {
      activities,
      loadingActivities,
      hasMoreActivities,
      selectedActivity,
      setSelectedActivity,
      loadMoreActivities,
      recentActiveCount,
    },
    indemnity: {
      bonds: missingIndemnityBonds,
      loading: loadingIndemnity,
      markingIndemnity,
      handleMarkIndemnityClick,
    },
    dialogs: {
      indemnityDialogOpen,
      closeIndemnityDialog,
      selectedBondForIndemnity,
      indemnityOption,
      setIndemnityOption,
      selectedFiles,
      isSubmitting,
      validationOpen,
      setValidationOpen,
      validationResults,
      processingOpen,
      handleMarkIndemnityOffline,
      handleSubmitIndemnityFiles,
      handleFinalizeIndemnitySubmit,
      isFinalizing,
      handleFilesSelected,
      handleClearFiles,
      handleRemoveFile,
      handleViewFile,
    },
    compliance: complianceFlags,
  };
}

export { buildBondsFeedUrl, buildMissingIndemnityUrl };
